import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";
import { Link, useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import {
  FiArrowLeft,
  FiChevronRight,
  FiSettings,
  FiList,
  FiRefreshCw,
  FiSave,
  FiSearch,
  FiCopy,
  FiCheck,
  FiX,
  FiClock,
  FiTrendingUp,
  FiAward,
  FiZap,
  FiShield,
  FiSliders,
  FiEye,
  FiExternalLink,
  FiLayers,
  FiCalendar,
} from "react-icons/fi";
import {
  get2048Settings,
  update2048Settings,
  get2048History,
  get2048Trends,
} from "../api/game2048Api";
import { getEvents } from "../api/eventApi";
import dayjs from "dayjs";
import GameAnalyticsCharts from "./components/GameAnalyticsCharts";

const formatDateTime = (dateStr) => {
  if (!dateStr) return "—";
  try {
    const d = new Date(dateStr);
    return d.toLocaleString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  } catch {
    return String(dateStr);
  }
};

export default function Game2048() {
  const navigate = useNavigate();

  const handleGoBack = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate("/games");
    }
  };

  // Tabs: "settings" | "history" | "trends"
  const [activeTab, setActiveTab] = useState("settings");

  // ==========================================
  // ⚙️ 1. Settings State
  // ==========================================
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [formSettings, setFormSettings] = useState({
    scoreUnit: 100,
    pointsPerUnit: 2,
    maxPointsPerRound: 1000,
    maxScorePerRound: 200000,
    minPlaySeconds: 10,
    cooldownSeconds: 0,
    isActive: true,
  });

  // Calculation Simulator helper
  const [testScore, setTestScore] = useState(5000);
  const calculatedPoints = useMemo(() => {
    const sUnit = Number(formSettings.scoreUnit) || 1;
    const pUnit = Number(formSettings.pointsPerUnit) || 0;
    const maxPts = Number(formSettings.maxPointsPerRound) || 1000;
    const score = Math.max(0, Number(testScore) || 0);
    const raw = Math.floor(score / sUnit) * pUnit;
    return Math.min(raw, maxPts);
  }, [testScore, formSettings.scoreUnit, formSettings.pointsPerUnit, formSettings.maxPointsPerRound]);

  // ==========================================
  // 📜 2. Audit History State
  // ==========================================
  const [historyLoading, setHistoryLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 1,
    page: 1,
    perPage: 20,
  });

  // Session Detail Modal
  const [selectedSession, setSelectedSession] = useState(null);
  const [copiedSessionId, setCopiedSessionId] = useState("");
  const searchDebounceTimer = useRef(null);

  // Events Selector
  const [eventsList, setEventsList] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState("all");

  // Analytics for Charts (same as Slots Game)
  const [analyticsHistory, setAnalyticsHistory] = useState([]);
  const [dailyStats, setDailyStats] = useState([]);

  // Resolve active/selected event details
  const activeRunningEvent = useMemo(() => {
    if (!Array.isArray(eventsList)) return null;
    return eventsList.find((e) => e.status?.toLowerCase() === "running");
  }, [eventsList]);

  const selectedEventObj = useMemo(() => {
    if (!selectedEventId || selectedEventId === "all" || !Array.isArray(eventsList)) return null;
    return eventsList.find(
      (e) => String(e.eventId) === String(selectedEventId) || String(e.id) === String(selectedEventId)
    );
  }, [selectedEventId, eventsList]);

  const selectedEventName = selectedEventObj
    ? selectedEventObj.title || selectedEventObj.name || selectedEventObj.eventName || `Event #${selectedEventObj.eventId || selectedEventObj.id}`
    : selectedEventId === "all"
    ? "All Events"
    : "Active Event";

  // ==========================================
  // 📈 3. Trends & Analytics State
  // ==========================================
  const [trendsDays, setTrendsDays] = useState(7);
  const [trendsLoading, setTrendsLoading] = useState(false);
  const [trendsData, setTrendsData] = useState([]);
  const [trendsSummary, setTrendsSummary] = useState({
    totalRounds: 0,
    uniquePlayers: 0,
    totalPoints: 0,
    avgScore: 0,
  });

  // Top KPI Stats
  const [stats, setStats] = useState({
    todaySessions: 0,
    todayPoints: 0,
    totalSessions: 0,
    eventTotalSessions: 0,
    totalPoints: 0,
    eventTotalPoints: 0,
    completedSessions: 0,
  });

  // Debounce search
  useEffect(() => {
    if (searchDebounceTimer.current) clearTimeout(searchDebounceTimer.current);
    searchDebounceTimer.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => {
      if (searchDebounceTimer.current) clearTimeout(searchDebounceTimer.current);
    };
  }, [search]);

  // Load Events and Initial Wide Chart Sample on Mount
  useEffect(() => {
    async function initEventsAndData() {
      let defaultId = "all";
      try {
        const res = await getEvents({ limit: 100, page: 1, status: "all" });
        const list = res?.data?.events || (Array.isArray(res?.data) ? res.data : []);
        setEventsList(list);

        const runningEvent = list.find((e) => e.status?.toLowerCase() === "running");
        if (runningEvent) {
          defaultId = String(runningEvent.eventId || runningEvent.id);
        }
      } catch (e) {
        console.warn("Could not load events:", e.message);
      }

      setSelectedEventId(defaultId);
      fetchSettings();
      fetchHistory(false, defaultId);

      // Wider history sample for charts (same as Slots Game)
      get2048History({ page: 1, perPage: 100, eventId: defaultId !== "all" ? defaultId : undefined })
        .then((res) => {
          if (res?.status) {
            const list = Array.isArray(res.data)
              ? res.data
              : Array.isArray(res.data?.history)
              ? res.data.history
              : [];
            if (list.length > 0) setAnalyticsHistory(list);
            if (Array.isArray(res.dailyStats) && res.dailyStats.length > 0) {
              setDailyStats(res.dailyStats);
            }
          }
        })
        .catch(() => {});

      get2048Trends({ days: 30 })
        .then((res) => {
          if (res?.status && res.data) {
            const trendList = Array.isArray(res.data)
              ? res.data
              : Array.isArray(res.data?.trends)
              ? res.data.trends
              : [];
            setTrendsData(trendList);
            setDailyStats((prev) => (prev.length > 0 ? prev : trendList));
          }
        })
        .catch(() => {});
    }
    initEventsAndData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ==========================================
  // 🚀 Load 2048 Settings
  // ==========================================
  const fetchSettings = useCallback(async () => {
    setSettingsLoading(true);
    try {
      const res = await get2048Settings();
      if (res?.status && res.data) {
        const d = res.data;
        setFormSettings({
          scoreUnit: Number(d.scoreUnit ?? 100),
          pointsPerUnit: Number(d.pointsPerUnit ?? 2),
          maxPointsPerRound: Number(d.maxPointsPerRound ?? 1000),
          maxScorePerRound: Number(d.maxScorePerRound ?? 200000),
          minPlaySeconds: Number(d.minPlaySeconds ?? 10),
          cooldownSeconds: Number(d.cooldownSeconds ?? 0),
          isActive: Boolean(d.isActive),
        });
      }
    } catch (err) {
      console.warn("Could not load 2048 settings:", err.message);
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  // Save Settings
  const handleSaveSettings = async (e) => {
    if (e) e.preventDefault();
    setSavingSettings(true);
    try {
      const res = await update2048Settings(formSettings);
      if (res?.status) {
        toast.success(res.message || "2048 game settings updated successfully!");
        fetchSettings();
      } else {
        toast.error(res?.message || "Failed to update settings");
      }
    } catch (err) {
      toast.error(err.message || "Failed to update 2048 settings");
    } finally {
      setSavingSettings(false);
    }
  };

  // ==========================================
  // 📜 Load Audit History
  // ==========================================
  const fetchHistory = useCallback(async (showToast = false, overrideEventId = null) => {
    setHistoryLoading(true);
    const evtId = overrideEventId !== null ? overrideEventId : selectedEventId;
    try {
      const res = await get2048History({
        page,
        perPage,
        search: debouncedSearch,
        status: statusFilter,
        eventId: evtId !== "all" ? evtId : undefined,
      });

      if (res?.status) {
        const list = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.history)
          ? res.data.history
          : [];

        setHistory(list);

        const serverPagination = res.pagination || res.data?.pagination || {};
        const totalRecords =
          typeof serverPagination.total === "number"
            ? serverPagination.total
            : typeof res.total === "number"
            ? res.total
            : list.length;

        const totalPages =
          typeof serverPagination.totalPages === "number"
            ? serverPagination.totalPages
            : Math.max(1, Math.ceil(totalRecords / perPage));

        setPagination({
          total: totalRecords,
          totalPages,
          page,
          perPage,
        });

        // Compute local quick KPI stats
        const todayStr = dayjs().format("YYYY-MM-DD");
        const todayRows = list.filter((r) => {
          const d = r.createdAt || r.completedAt || r.startedAt;
          return d && dayjs(d).format("YYYY-MM-DD") === todayStr;
        });

        const todayPointsCalc = todayRows.reduce(
          (acc, r) => acc + (Number(r.claimedPoints ?? r.eventPoints ?? r.pointsAwarded ?? r.points ?? 0) || 0),
          0
        );

        const totalPtsCalc = list.reduce(
          (acc, r) => acc + (Number(r.claimedPoints ?? r.eventPoints ?? r.pointsAwarded ?? r.points ?? 0) || 0),
          0
        );

        setStats({
          todaySessions: res.stats?.todaySessions ?? todayRows.length,
          todayPoints: res.stats?.todayPoints ?? todayPointsCalc,
          totalSessions: totalRecords,
          eventTotalSessions: totalRecords,
          totalPoints: res.stats?.totalPoints ?? totalPtsCalc,
          eventTotalPoints: res.stats?.totalPoints ?? totalPtsCalc,
          completedSessions: list.filter((r) => r.status === "completed").length,
        });

        if (showToast) toast.success("2048 history refreshed!");
      }
    } catch (err) {
      console.warn("Error fetching 2048 history:", err.message);
      if (showToast) toast.error(err.message || "Failed to load history");
    } finally {
      setHistoryLoading(false);
    }
  }, [page, perPage, debouncedSearch, statusFilter, selectedEventId]);

  const handleEventChange = (newEventId) => {
    setSelectedEventId(newEventId);
    setPage(1);
    fetchHistory(false, newEventId);
    get2048History({ page: 1, perPage: 100, eventId: newEventId !== "all" ? newEventId : undefined })
      .then((res) => {
        if (res?.status) {
          const list = Array.isArray(res.data)
            ? res.data
            : Array.isArray(res.data?.history)
            ? res.data.history
            : [];
          if (list.length > 0) setAnalyticsHistory(list);
          if (Array.isArray(res.dailyStats) && res.dailyStats.length > 0) {
            setDailyStats(res.dailyStats);
          }
        }
      })
      .catch(() => {});
  };

  // ==========================================
  // 📈 Load Trends Data
  // ==========================================
  const fetchTrends = useCallback(async () => {
    setTrendsLoading(true);
    try {
      const res = await get2048Trends({ days: trendsDays });
      if (res?.status && res.data) {
        const trendList = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.trends)
          ? res.data.trends
          : [];

        setTrendsData(trendList);

        // Aggregate summary metrics
        let roundsSum = 0;
        let pointsSum = 0;
        let scoreSum = 0;
        const playersSet = new Set();

        trendList.forEach((row) => {
          roundsSum += Number(row.rounds || row.sessions || row.count || 0);
          pointsSum += Number(row.points || row.totalPoints || row.pointsDistributed || 0);
          scoreSum += Number(row.avgScore || row.score || 0);
          if (row.uniquePlayers != null) {
            playersSet.add(Number(row.uniquePlayers));
          }
        });

        setTrendsSummary({
          totalRounds: res.data?.totalRounds ?? roundsSum,
          uniquePlayers: res.data?.uniquePlayers ?? (playersSet.size > 0 ? Array.from(playersSet).reduce((a, b) => a + b, 0) : roundsSum),
          totalPoints: res.data?.totalPoints ?? pointsSum,
          avgScore: trendList.length > 0 ? Math.round(scoreSum / trendList.length) : 0,
        });
      }
    } catch (err) {
      console.warn("Could not load 2048 trends:", err.message);
    } finally {
      setTrendsLoading(false);
    }
  }, [trendsDays]);

  // Initial Load
  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    if (activeTab === "history") {
      fetchHistory();
    } else if (activeTab === "trends") {
      fetchTrends();
    }
  }, [activeTab, fetchHistory, fetchTrends]);

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(String(text));
    setCopiedSessionId(id);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopiedSessionId(""), 2000);
  };



  return (
    <div className="flex bg-gray-100 min-h-screen">
      <Sidebar />

      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />

        {/* Full-width container matching Boom Cards & 777 Slots */}
        <div className="p-5 sm:p-7 space-y-5 w-full">
          {/* Breadcrumb Navigation & Back Button */}
          <div className="flex items-center gap-2 text-xs md:text-sm text-gray-500">
            <button
              onClick={handleGoBack}
              className="flex items-center gap-1 hover:text-black transition cursor-pointer p-1 -ml-1 rounded hover:bg-gray-200"
              title="Go back"
            >
              <FiArrowLeft className="h-4 w-4" />
              <span>Back</span>
            </button>
            <FiChevronRight className="h-3 w-3 text-gray-400" />
            <Link to="/dashboard" className="hover:text-black transition">
              Dashboard
            </Link>
            <FiChevronRight className="h-3 w-3 text-gray-400" />
            <Link to="/games" className="hover:text-black transition flex items-center gap-1">
              <span>Games Management</span>
            </Link>
            <FiChevronRight className="h-3 w-3 text-gray-400" />
            <span className="text-amber-700 font-bold">2048 Game</span>
          </div>

          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-amber-100 text-amber-800 flex items-center justify-center text-2xl shadow-xs">
                🧩
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl font-black text-gray-900 tracking-tight">
                    2048 Game Management
                  </h1>
                  <span
                    className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase border ${
                      formSettings.isActive
                        ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                        : "bg-rose-100 text-rose-800 border-rose-300"
                    }`}
                  >
                    <span
                      className={`w-1.5 h-1.5 rounded-full ${
                        formSettings.isActive ? "bg-emerald-500 animate-pulse" : "bg-rose-500"
                      }`}
                    />
                    {formSettings.isActive ? "Live" : "Disabled"}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-0.5">
                  Configure scoring unit rules, per-round point caps, anti-cheat limits, and play audit logs.
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2.5">
              <button
                onClick={() => {
                  if (activeTab === "settings") fetchSettings();
                  else fetchHistory(true);
                  toast.success("Data refreshed");
                }}
                className="flex items-center gap-1.5 text-xs font-bold text-gray-700 bg-white hover:bg-gray-50 border border-gray-200 px-3.5 py-2 rounded-xl transition shadow-2xs cursor-pointer"
                title="Refresh"
              >
                <FiRefreshCw className="w-3.5 h-3.5" />
                <span>Refresh</span>
              </button>
            </div>
          </div>

          {/* 5 Core Items in Single Unified Row (Event Selector + 4 Key Metrics - same as Slots Game) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3.5">
            {/* 1. Event Selector Card */}
            <div className="bg-white rounded-2xl shadow-xs border border-amber-200/80 bg-gradient-to-br from-white to-amber-50/20 p-4 flex flex-col justify-between hover:border-amber-300 transition">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-amber-800 uppercase tracking-wider flex items-center gap-1.5">
                  <FiCalendar size={13} className="text-amber-600" />
                  <span>Select Event</span>
                </span>
                {selectedEventObj?.status && (
                  <span
                    className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase ${
                      selectedEventObj.status.toLowerCase() === "running"
                        ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
                        : "bg-gray-100 text-gray-600 border border-gray-200"
                    }`}
                  >
                    {selectedEventObj.status}
                  </span>
                )}
              </div>
              <div className="mt-1.5">
                <select
                  value={selectedEventId}
                  onChange={(e) => handleEventChange(e.target.value)}
                  className="w-full bg-white hover:bg-gray-50 border border-gray-300 text-gray-900 font-bold text-xs rounded-xl px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#ffff00] cursor-pointer shadow-2xs"
                >
                  {activeRunningEvent && (
                    <option value={activeRunningEvent.eventId || activeRunningEvent.id}>
                      🟢 {activeRunningEvent.title || activeRunningEvent.name || activeRunningEvent.eventName || `Event #${activeRunningEvent.id}`}
                    </option>
                  )}
                  <option value="all">🏆 All Events (Global)</option>
                  {(Array.isArray(eventsList) ? eventsList : [])
                    .filter(
                      (ev) =>
                        !activeRunningEvent ||
                        String(ev.eventId || ev.id) !==
                          String(activeRunningEvent.eventId || activeRunningEvent.id)
                    )
                    .map((ev) => {
                      const isCompleted = ev.status?.toLowerCase() === "completed";
                      return (
                        <option key={ev.eventId || ev.id} value={ev.eventId || ev.id}>
                          {isCompleted ? "⚪ " : "🟡 "}
                          {ev.title || ev.name || ev.eventName || `Event #${ev.id}`}
                        </option>
                      );
                    })}
                </select>
              </div>
              <p className="text-[11px] text-gray-400 mt-1.5 font-medium truncate">
                Filter metrics &amp; charts
              </p>
            </div>

            {/* 2. Today Total Session */}
            <div className="bg-white rounded-2xl shadow-xs border border-gray-200/80 p-4 flex flex-col justify-between hover:border-gray-300 transition">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                  Today Total Sessions
                </span>
                <div className="w-6 h-6 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                  <FiClock size={13} />
                </div>
              </div>
              <p className="text-2xl font-black text-gray-900 font-mono mt-1.5">
                {(stats?.todaySessions ?? 0).toLocaleString()}
              </p>
              <p className="text-[11px] text-gray-400 mt-1.5 font-medium">
                Rounds played today (UTC)
              </p>
            </div>

            {/* 3. Today's Awarded Points */}
            <div className="bg-white rounded-2xl shadow-xs border border-emerald-200/80 bg-gradient-to-br from-white to-emerald-50/20 p-4 flex flex-col justify-between hover:border-emerald-300 transition">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-emerald-800 uppercase tracking-wider">
                  Today's Awarded Points
                </span>
                <div className="w-6 h-6 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold">
                  <FiZap size={13} />
                </div>
              </div>
              <p className="text-2xl font-black text-emerald-700 font-mono mt-1.5">
                +{(stats?.todayPoints ?? 0).toLocaleString()}{" "}
                <span className="text-[11px] font-sans text-emerald-600 font-bold">PTS</span>
              </p>
              <p className="text-[11px] text-emerald-700/80 mt-1.5 font-medium">
                Claimed by players today
              </p>
            </div>

            {/* 4. Event's Total Points */}
            <div className="bg-white rounded-2xl shadow-xs border border-purple-200/80 bg-gradient-to-br from-white to-purple-50/20 p-4 flex flex-col justify-between hover:border-purple-300 transition">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-purple-800 uppercase tracking-wider">
                  Event's Total Points
                </span>
                <div className="w-6 h-6 rounded-lg bg-purple-100 text-purple-700 flex items-center justify-center font-bold">
                  <FiAward size={13} />
                </div>
              </div>
              <p className="text-2xl font-black text-purple-700 font-mono mt-1.5">
                {(stats?.eventTotalPoints ?? stats?.totalPoints ?? 0).toLocaleString()}{" "}
                <span className="text-[11px] font-sans text-purple-600 font-bold">PTS</span>
              </p>
              <p className="text-[11px] text-purple-700/80 mt-1.5 font-medium truncate">
                Cumulative in {selectedEventName}
              </p>
            </div>

            {/* 5. Event's Total Sessions */}
            <div className="bg-white rounded-2xl shadow-xs border border-amber-200/80 bg-gradient-to-br from-white to-amber-50/20 p-4 flex flex-col justify-between hover:border-amber-300 transition">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-amber-800 uppercase tracking-wider">
                  Event's Total Sessions
                </span>
                <div className="w-6 h-6 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center font-bold">
                  <FiLayers size={13} />
                </div>
              </div>
              <p className="text-2xl font-black text-amber-700 font-mono mt-1.5">
                {(stats?.eventTotalSessions ?? stats?.totalSessions ?? 0).toLocaleString()}
              </p>
              <p className="text-[11px] text-amber-700/80 mt-1.5 font-medium truncate">
                All rounds in {selectedEventName}
              </p>
            </div>
          </div>

          {/* 2 CHARTS (Update on date change, default to active event - same as Slots Game) */}
          <GameAnalyticsCharts
            history={analyticsHistory.length > 0 ? analyticsHistory : history}
            dailyStats={dailyStats}
            stats={stats}
            gameName="2048"
            themeColor="amber"
          />

          {/* SUB-NAVIGATION TABS (Game Settings & Rules, Audit History & Sessions) */}
          <div className="flex gap-2 bg-white border border-gray-200 rounded-xl p-1.5 w-fit shadow-xs">
            <button
              type="button"
              onClick={() => setActiveTab("settings")}
              className={`px-5 py-2 text-xs font-bold rounded-lg transition flex items-center gap-2 cursor-pointer ${
                activeTab === "settings"
                  ? "bg-[#ffff00] text-black shadow-2xs"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
              }`}
            >
              <FiSettings size={14} />
              <span>Game Settings &amp; Rules</span>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab("history")}
              className={`px-5 py-2 text-xs font-bold rounded-lg transition flex items-center gap-2 cursor-pointer ${
                activeTab === "history"
                  ? "bg-[#ffff00] text-black shadow-2xs"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
              }`}
            >
              <FiList size={14} />
              <span>Audit History &amp; Sessions</span>
            </button>
          </div>

          {/* ========================================================================= */}
          {/* TAB 1: SETTINGS & RULES */}
          {/* ========================================================================= */}
          {activeTab === "settings" && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Form Settings Panel */}
              <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-200/80 p-6 shadow-2xs space-y-6">
                <div className="flex items-center justify-between border-b border-gray-100 pb-4">
                  <div>
                    <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
                      <FiSliders className="w-5 h-5 text-amber-600" />
                      <span>2048 Game Rules Configuration</span>
                    </h2>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Tune base unit point calculation, session ceilings, and cooldowns.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs font-bold text-gray-700">Status:</label>
                    <button
                      type="button"
                      onClick={() =>
                        setFormSettings({ ...formSettings, isActive: !formSettings.isActive })
                      }
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
                        formSettings.isActive ? "bg-emerald-500" : "bg-gray-300"
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          formSettings.isActive ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                    <span className="text-xs font-bold text-gray-800">
                      {formSettings.isActive ? "Active" : "Disabled"}
                    </span>
                  </div>
                </div>

                {settingsLoading ? (
                  <div className="py-12 flex flex-col items-center justify-center gap-2 text-gray-400">
                    <FiRefreshCw className="w-6 h-6 animate-spin text-amber-500" />
                    <span className="text-xs">Loading 2048 settings...</span>
                  </div>
                ) : (
                <form onSubmit={handleSaveSettings} className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Score Unit */}
                    <div>
                      <label className="block text-xs font-bold text-gray-800 mb-1">
                        Score Unit (Base Score)
                      </label>
                      <input
                        type="number"
                        min={1}
                        required
                        value={formSettings.scoreUnit}
                        onChange={(e) =>
                          setFormSettings({
                            ...formSettings,
                            scoreUnit: Math.max(1, Number(e.target.value)),
                          })
                        }
                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-900 focus:outline-none focus:border-yellow-400 focus:bg-white"
                      />
                      <p className="text-[11px] text-gray-500 mt-1">
                        Base score unit threshold (e.g. 100 score).
                      </p>
                    </div>

                    {/* Points Per Unit */}
                    <div>
                      <label className="block text-xs font-bold text-gray-800 mb-1">
                        Points Per Unit
                      </label>
                      <input
                        type="number"
                        min={1}
                        required
                        value={formSettings.pointsPerUnit}
                        onChange={(e) =>
                          setFormSettings({
                            ...formSettings,
                            pointsPerUnit: Math.max(1, Number(e.target.value)),
                          })
                        }
                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-900 focus:outline-none focus:border-yellow-400 focus:bg-white"
                      />
                      <p className="text-[11px] text-gray-500 mt-1">
                        Event points credited per base score unit (e.g. 2 points).
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Max Points Per Round */}
                    <div>
                      <label className="block text-xs font-bold text-gray-800 mb-1">
                        Max Points Per Round (Cap)
                      </label>
                      <input
                        type="number"
                        min={1}
                        required
                        value={formSettings.maxPointsPerRound}
                        onChange={(e) =>
                          setFormSettings({
                            ...formSettings,
                            maxPointsPerRound: Math.max(1, Number(e.target.value)),
                          })
                        }
                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-900 focus:outline-none focus:border-yellow-400 focus:bg-white font-mono"
                      />
                      <p className="text-[11px] text-gray-500 mt-1">
                        Maximum points allowed to earn in a single round (e.g. 1,000).
                      </p>
                    </div>

                    {/* Max Score Per Round (Anti-Cheat) */}
                    <div>
                      <label className="block text-xs font-bold text-gray-800 mb-1 flex items-center gap-1.5">
                        <FiShield className="w-3.5 h-3.5 text-rose-500" />
                        <span>Max Score Per Round (Anti-Cheat)</span>
                      </label>
                      <input
                        type="number"
                        min={100}
                        required
                        value={formSettings.maxScorePerRound}
                        onChange={(e) =>
                          setFormSettings({
                            ...formSettings,
                            maxScorePerRound: Math.max(100, Number(e.target.value)),
                          })
                        }
                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-900 focus:outline-none focus:border-yellow-400 focus:bg-white font-mono"
                      />
                      <p className="text-[11px] text-gray-500 mt-1">
                        Scores above this ceiling get flagged or disqualified.
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Min Play Duration */}
                    <div>
                      <label className="block text-xs font-bold text-gray-800 mb-1 flex items-center gap-1.5">
                        <FiClock className="w-3.5 h-3.5 text-amber-500" />
                        <span>Min Play Time (Seconds)</span>
                      </label>
                      <input
                        type="number"
                        min={0}
                        max={600}
                        required
                        value={formSettings.minPlaySeconds}
                        onChange={(e) =>
                          setFormSettings({
                            ...formSettings,
                            minPlaySeconds: Math.max(0, Number(e.target.value)),
                          })
                        }
                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-900 focus:outline-none focus:border-yellow-400 focus:bg-white"
                      />
                      <p className="text-[11px] text-gray-500 mt-1">
                        Minimum play duration required before score can be submitted.
                      </p>
                    </div>

                    {/* Cooldown Seconds */}
                    <div>
                      <label className="block text-xs font-bold text-gray-800 mb-1 flex items-center gap-1.5">
                        <FiZap className="w-3.5 h-3.5 text-blue-500" />
                        <span>Cooldown Break Time (Seconds)</span>
                      </label>
                      <input
                        type="number"
                        min={0}
                        max={86400}
                        required
                        value={formSettings.cooldownSeconds}
                        onChange={(e) =>
                          setFormSettings({
                            ...formSettings,
                            cooldownSeconds: Math.max(0, Number(e.target.value)),
                          })
                        }
                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-900 focus:outline-none focus:border-yellow-400 focus:bg-white"
                      />
                      <p className="text-[11px] text-gray-500 mt-1">
                        Mandatory wait time between consecutive rounds (0 = no cooldown).
                      </p>
                    </div>
                  </div>

                  <div className="pt-3 border-t border-gray-100 flex items-center justify-end">
                    <button
                      type="submit"
                      disabled={savingSettings}
                      className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-black text-[#ffff00] font-bold text-sm hover:bg-gray-800 transition shadow-md disabled:opacity-50 cursor-pointer"
                    >
                      {savingSettings ? (
                        <>
                          <FiRefreshCw className="w-4 h-4 animate-spin" />
                          <span>Saving Changes...</span>
                        </>
                      ) : (
                        <>
                          <FiSave className="w-4 h-4" />
                          <span>Save 2048 Settings</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
                )}
              </div>

              {/* Side Simulation / Live Calculation Preview */}
              <div className="space-y-6">
                <div className="bg-white rounded-2xl border border-gray-200/80 p-5 shadow-2xs">
                  <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2 mb-3">
                    <FiAward className="w-4 h-4 text-amber-500" />
                    <span>Live Reward Calculator</span>
                  </h3>
                  <p className="text-xs text-gray-500 mb-4">
                    Test the points formula with hypothetical user scores to verify your rule configuration.
                  </p>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-[11px] font-bold text-gray-700 mb-1">
                        Test Player Score:
                      </label>
                      <input
                        type="number"
                        min={0}
                        step={100}
                        value={testScore}
                        onChange={(e) => setTestScore(Number(e.target.value))}
                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-sm font-mono text-gray-900 focus:outline-none focus:border-yellow-400"
                      />
                    </div>

                    <div className="bg-amber-50/80 border border-amber-200/60 rounded-xl p-3 space-y-1.5 text-xs">
                      <div className="flex justify-between text-gray-600">
                        <span>Units Reached:</span>
                        <span className="font-mono font-bold text-gray-900">
                          {Math.floor(testScore / (formSettings.scoreUnit || 1))} units
                        </span>
                      </div>
                      <div className="flex justify-between text-gray-600">
                        <span>Raw Points:</span>
                        <span className="font-mono font-bold text-gray-900">
                          {Math.floor(testScore / (formSettings.scoreUnit || 1)) * formSettings.pointsPerUnit} pts
                        </span>
                      </div>
                      <div className="flex justify-between text-gray-600">
                        <span>Round Cap:</span>
                        <span className="font-mono font-bold text-gray-900">
                          {formSettings.maxPointsPerRound} pts
                        </span>
                      </div>
                      <div className="pt-2 border-t border-amber-200 flex justify-between items-center">
                        <span className="font-bold text-gray-900">Final Event Points:</span>
                        <span className="text-base font-black text-amber-700 font-mono">
                          +{calculatedPoints.toLocaleString()} pts
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50/70 border border-blue-200/60 rounded-2xl p-5 shadow-2xs text-xs text-blue-900 space-y-2">
                  <div className="font-bold flex items-center gap-1.5 text-blue-950">
                    <FiShield className="w-4 h-4 text-blue-600" />
                    <span>Anti-Cheat & Validation Rules</span>
                  </div>
                  <ul className="list-disc list-inside space-y-1 text-blue-800 text-[11px]">
                    <li>Rounds submitted under {formSettings.minPlaySeconds}s are flagged.</li>
                    <li>Scores above {formSettings.maxScorePerRound.toLocaleString()} are rejected.</li>
                    <li>Points credit directly to user's event balance in real time.</li>
                    <li>All completions appear in User Coin History under Mini Games.</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* ========================================================================= */}
          {/* TAB 2: AUDIT HISTORY & SESSIONS */}
          {/* ========================================================================= */}
          {activeTab === "history" && (
            <div className="bg-white rounded-2xl border border-gray-200/80 shadow-2xs overflow-hidden space-y-4 p-5">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="relative flex-1 max-w-md">
                  <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search User ID, Username, or Session ID..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-900 focus:outline-none focus:border-yellow-400 focus:bg-white"
                  />
                  {search && (
                    <button
                      onClick={() => setSearch("")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 cursor-pointer"
                    >
                      <FiX className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  {/* Status Filters */}
                  <div className="flex items-center bg-gray-100 p-1 rounded-xl text-xs">
                    {["all", "completed", "started", "disqualified"].map((st) => (
                      <button
                        key={st}
                        onClick={() => {
                          setStatusFilter(st);
                          setPage(1);
                        }}
                        className={`px-3 py-1 rounded-lg font-bold capitalize transition cursor-pointer ${
                          statusFilter === st
                            ? "bg-white text-black shadow-2xs"
                            : "text-gray-500 hover:text-gray-900"
                        }`}
                      >
                        {st}
                      </button>
                    ))}
                  </div>

                  <button
                    onClick={fetchHistory}
                    className="p-2 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 cursor-pointer"
                    title="Reload History"
                  >
                    <FiRefreshCw className={`w-4 h-4 ${historyLoading ? "animate-spin" : ""}`} />
                  </button>
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto border border-gray-100 rounded-xl">
                <table className="w-full text-left text-xs">
                  <thead className="bg-gray-50 text-gray-500 font-bold uppercase tracking-wider text-[10px] border-b border-gray-200">
                    <tr>
                      <th className="py-3 px-4">Session ID / Time</th>
                      <th className="py-3 px-4">User</th>
                      <th className="py-3 px-4">Event</th>
                      <th className="py-3 px-4 text-right">Game Score</th>
                      <th className="py-3 px-4 text-right">Event Points</th>
                      <th className="py-3 px-4 text-center">Duration</th>
                      <th className="py-3 px-4 text-center">Status</th>
                      <th className="py-3 px-4 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {historyLoading ? (
                      <tr>
                        <td colSpan={8} className="py-12 text-center text-gray-400">
                          <FiRefreshCw className="w-6 h-6 animate-spin mx-auto mb-2 text-amber-500" />
                          <span>Loading 2048 audit sessions...</span>
                        </td>
                      </tr>
                    ) : history.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="py-12 text-center text-gray-400">
                          No 2048 gameplay sessions found for selected filters.
                        </td>
                      </tr>
                    ) : (
                      history.map((row) => {
                        const sid = row.sessionId || row.id || row.gameSessionId || "—";
                        const uid = row.userId || row.user_id || row.user?.id || "—";
                        const uname =
                          row.user?.username ||
                          row.username ||
                          row.user?.name ||
                          `User #${uid}`;
                        const score = Number(row.score ?? row.gameScore ?? 0);
                        const pts = Number(
                          row.claimedPoints ?? row.eventPoints ?? row.pointsAwarded ?? row.points ?? 0
                        );
                        const durationSec = Number(
                          row.durationSeconds ?? row.duration ?? row.playSeconds ?? 0
                        );
                        const statusStr = String(row.status || "completed").toLowerCase();

                        return (
                          <tr key={sid} className="hover:bg-gray-50 transition">
                            <td className="py-3 px-4 whitespace-nowrap">
                              <div className="flex items-center gap-1.5 font-mono text-gray-900 font-bold">
                                <span>{String(sid).slice(0, 14)}...</span>
                                <button
                                  onClick={() => copyToClipboard(sid, sid)}
                                  className="text-gray-400 hover:text-gray-800 p-0.5"
                                  title="Copy Session ID"
                                >
                                  {copiedSessionId === sid ? (
                                    <FiCheck className="w-3 h-3 text-emerald-600" />
                                  ) : (
                                    <FiCopy className="w-3 h-3" />
                                  )}
                                </button>
                              </div>
                              <div className="text-[10px] text-gray-400 mt-0.5">
                                {formatDateTime(row.createdAt || row.completedAt || row.timestamp)}
                              </div>
                            </td>

                            <td className="py-3 px-4">
                              <Link
                                to={`/user/${uid}/coin-history`}
                                className="font-bold text-gray-900 hover:text-amber-600 transition flex items-center gap-1.5"
                              >
                                <span>{uname}</span>
                                <FiExternalLink className="w-3 h-3 text-gray-400" />
                              </Link>
                              <div className="text-[10px] text-gray-400">ID: #{uid}</div>
                            </td>

                            <td className="py-3 px-4 text-gray-600">
                              <span className="font-medium text-gray-800">
                                {row.eventTitle || row.eventName || (row.eventId ? `Event #${row.eventId}` : "Zeta Rush")}
                              </span>
                            </td>

                            <td className="py-3 px-4 text-right font-mono font-bold text-gray-900">
                              {score.toLocaleString()}
                            </td>

                            <td className="py-3 px-4 text-right">
                              <span className="font-mono font-black text-amber-700 bg-amber-50 px-2 py-0.5 rounded border border-amber-200/60">
                                +{pts.toLocaleString()} pts
                              </span>
                            </td>

                            <td className="py-3 px-4 text-center text-gray-600 font-mono">
                              {durationSec > 0 ? `${durationSec}s` : "—"}
                            </td>

                            <td className="py-3 px-4 text-center">
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                                  statusStr === "completed"
                                    ? "bg-emerald-100 text-emerald-800"
                                    : statusStr === "disqualified"
                                    ? "bg-rose-100 text-rose-800"
                                    : "bg-blue-100 text-blue-800"
                                }`}
                              >
                                {statusStr}
                              </span>
                            </td>

                            <td className="py-3 px-4 text-center">
                              <button
                                onClick={() => setSelectedSession(row)}
                                className="p-1.5 rounded-lg bg-gray-100 hover:bg-black hover:text-white text-gray-700 transition cursor-pointer"
                                title="View Details"
                              >
                                <FiEye className="w-3.5 h-3.5" />
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2 text-xs text-gray-500">
                <div className="flex items-center gap-2">
                  <span>Show</span>
                  <select
                    value={perPage}
                    onChange={(e) => {
                      setPerPage(Number(e.target.value));
                      setPage(1);
                    }}
                    className="bg-white border border-gray-200 rounded-lg px-2 py-1 text-xs"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span>records per page (Total {pagination.total.toLocaleString()} sessions)</span>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1 || historyLoading}
                    className="px-3 py-1 bg-white border border-gray-200 rounded-lg font-bold text-gray-700 hover:bg-gray-100 disabled:opacity-40 cursor-pointer"
                  >
                    Prev
                  </button>
                  <span className="font-bold text-gray-800">
                    Page {page} of {pagination.totalPages}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                    disabled={page >= pagination.totalPages || historyLoading}
                    className="px-3 py-1 bg-white border border-gray-200 rounded-lg font-bold text-gray-700 hover:bg-gray-100 disabled:opacity-40 cursor-pointer"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ========================================================================= */}
      {/* 🔍 SESSION DETAIL MODAL */}
      {/* ========================================================================= */}
      {selectedSession && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl space-y-4 border border-gray-200">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <div>
                <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                  <span>🧩 2048 Session Details</span>
                </h3>
                <p className="text-xs text-gray-400 font-mono mt-0.5">
                  ID: #{selectedSession.sessionId || selectedSession.id || "—"}
                </p>
              </div>
              <button
                onClick={() => setSelectedSession(null)}
                className="p-1 text-gray-400 hover:text-black rounded-lg cursor-pointer"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3 text-xs">
              <div className="bg-gray-50 rounded-xl p-3 grid grid-cols-2 gap-3">
                <div>
                  <span className="text-gray-500 block">User:</span>
                  <span className="font-bold text-gray-900">
                    {selectedSession.user?.username || selectedSession.username || `User #${selectedSession.userId}`}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 block">User ID:</span>
                  <span className="font-bold text-gray-900">#{selectedSession.userId}</span>
                </div>
                <div>
                  <span className="text-gray-500 block">Final Score:</span>
                  <span className="font-mono font-black text-base text-gray-900">
                    {Number(selectedSession.score || selectedSession.gameScore || 0).toLocaleString()}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 block">Event Points Credited:</span>
                  <span className="font-mono font-black text-base text-amber-600">
                    +{Number(selectedSession.claimedPoints ?? selectedSession.eventPoints ?? selectedSession.pointsAwarded ?? selectedSession.points ?? 0).toLocaleString()} pts
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 block">Duration:</span>
                  <span className="font-mono font-bold text-gray-800">
                    {selectedSession.durationSeconds || selectedSession.duration || 0} seconds
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 block">Status:</span>
                  <span className="font-bold uppercase text-emerald-700">
                    {selectedSession.status || "completed"}
                  </span>
                </div>
              </div>

              <div className="bg-amber-50/70 border border-amber-200/60 rounded-xl p-3 text-[11px] text-amber-900 space-y-1">
                <div className="font-bold">Event Association:</div>
                <div>{selectedSession.eventTitle || selectedSession.eventName || "Active Running Event"}</div>
                <div className="text-gray-500 mt-1">
                  Completed At: {formatDateTime(selectedSession.createdAt || selectedSession.completedAt)}
                </div>
              </div>
            </div>

            <div className="pt-2 flex justify-end gap-2 border-t border-gray-100">
              <Link
                to={`/user/${selectedSession.userId}/coin-history`}
                className="px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-800 font-bold text-xs flex items-center gap-1.5 transition"
              >
                <span>View User Ledger</span>
                <FiExternalLink className="w-3 h-3" />
              </Link>
              <button
                onClick={() => setSelectedSession(null)}
                className="px-4 py-2 rounded-xl bg-black text-[#ffff00] font-bold text-xs hover:bg-gray-800 transition cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
